import { z } from "zod";
import { ufSchema } from "./calculation.js";
import { fiscalFactDecisionScopeSchema, fiscalFactFieldNameSchema, fiscalFactKindSchema, fiscalFactOriginScopeSchema, fiscalFactPreviousScopeSchema, fiscalFactSchema, fiscalFactScopeAcknowledgementSchema, fiscalFactScopeRestrictionTargetSchema, } from "./fiscal-facts.js";
import { agreementChangesBetween, agreementMembersOn, derivedOriginScope, effectiveAgreements, } from "./st-agreements.js";
/**
 * Group review of UNVERIFIED fiscal facts (Regras → Verificação).
 *
 * A review group is the unit an operator opens: every UNVERIFIED fact read
 * from one preserved document version (evidence id + content hash), one
 * annex section (CEST segment), one destination UF, one origin scope and one
 * validity window. "Same segment" or "same value" alone never shares a
 * decision; a new document version is a different group.
 *
 * A review session fixes a server-generated manifest (fact id + revision)
 * and records what the reviewer inspected, which facts are exceptions and,
 * for the SAMPLE method, the reproducible sample of its policy
 * (`fiscal-fact-sample-v2` for new sessions; sessions started under
 * `fiscal-fact-sample-v1` keep that policy for their whole life and their
 * audit). Confirming a session sets VERIFIED on each
 * manifest fact whose revision still matches. It never certifies, never
 * publishes a rule and never reads IOB results as a decision: IOB
 * corroboration is shown next to the sample and nothing more.
 *
 * The pure functions below are shared by the API (the authority), the web
 * mock and the review desk, so the three compute the same groups, samples
 * and blockers.
 */
/** First policy: every flagged fact is mandatory, group-level ones included. */
export const FISCAL_FACT_SAMPLE_POLICY_V1 = "fiscal-fact-sample-v1";
/**
 * Current policy: a property every fact of the population shares (inferred
 * validity, one origin scope) is a group signal the reviewer acknowledges
 * once at confirmation, not a mandatory per-fact flag.
 */
export const FISCAL_FACT_SAMPLE_POLICY_V2 = "fiscal-fact-sample-v2";
/** The policy new sessions (and previews) use. */
export const FISCAL_FACT_SAMPLE_POLICY_ID = FISCAL_FACT_SAMPLE_POLICY_V2;
export const fiscalFactSamplePolicyIdSchema = z.enum([
    FISCAL_FACT_SAMPLE_POLICY_V1,
    FISCAL_FACT_SAMPLE_POLICY_V2,
]);
/** Largest manifest a session carries; larger groups are split by the UI filters. */
export const FISCAL_FACT_REVIEW_MAX_FACTS = 500;
/** Groups at or below this size are sampled whole (the sample is the group). */
export const FISCAL_FACT_SAMPLE_WHOLE_GROUP_MAX = 10;
export const FISCAL_FACT_SAMPLE_MIN = 10;
export const FISCAL_FACT_SAMPLE_MAX = 60;
export const FISCAL_FACT_SAMPLE_RATE = 0.15;
export const FISCAL_FACT_SAMPLE_MAX_EXPANSIONS = 20;
/** From this many discrepancies the SAMPLE method is closed: full review only. */
export const FISCAL_FACT_SAMPLE_DISCREPANCY_LIMIT = 2;
export const FISCAL_FACT_EXCEPTION_REASON_MIN = 3;
const uuid = z.string().uuid();
const sha256Schema = z.string().regex(/^[a-f0-9]{64}$/);
const ncmSchema = z.string().regex(/^\d{8}$/);
export const fiscalFactReviewGroupModeSchema = z.enum(["DOCUMENT", "SEGMENT_UF"]);
/** `d-…` groups by document/annex, `s-…` by segment × UF (16 hex, FNV-1a). */
export const fiscalFactReviewGroupIdSchema = z.string().regex(/^[ds]-[a-f0-9]{16}$/);
export const fiscalFactReviewMethodSchema = z.enum(["FULL", "SAMPLE"]);
/** Signals that put a fact in the mandatory part of any sample. */
export const fiscalFactReviewFlagSchema = z.enum([
    /** The annex row failed or escalated a worker check. */
    "CHECKS_FAILED",
    /** The source stated no start date: validFrom is the capture date. */
    "INFERRED_VALIDITY",
    /** No character locator (or no quote) ties the row to the document. */
    "MISSING_LOCATOR",
    /** Conflict, pending revalidation or a reviewer-reported discrepancy. */
    "DISCREPANCY",
    /** A newer capture of the same URL has a different content hash. */
    "DOCUMENT_CHANGED",
    /** A CERTIFIED IOB fact on the same NCM × route disagrees. */
    "IOB_MISMATCH",
]);
/**
 * Per-fact flags that describe the document rather than the row. Under
 * `fiscal-fact-sample-v2`, when every fact of the population carries one it
 * becomes a group signal (acknowledged once) instead of a mandatory flag.
 * A document/annex group keys on "stated start or inferred", so inferred
 * validity is always shared there.
 */
export const FISCAL_FACT_GROUP_LEVEL_FLAGS = ["INFERRED_VALIDITY"];
/**
 * Properties shared by every fact of a review population, acknowledged once
 * in the confirmation (`fiscal-fact-sample-v2`):
 * - INFERRED_VALIDITY: the document states no start; every validFrom is the
 *   capture/publication date.
 * - ORIGIN_SCOPE: every fact has the same origin scope (all origins, listed
 *   origins or internal only), so the decision applies to that scope.
 */
export const fiscalFactReviewGroupSignalSchema = z.enum(["INFERRED_VALIDITY", "ORIGIN_SCOPE"]);
export const fiscalFactReviewGroupKeySchema = z
    .object({
    mode: fiscalFactReviewGroupModeSchema,
    kind: fiscalFactKindSchema,
    /** DOCUMENT mode: the preserved document version (null in SEGMENT_UF). */
    evidenceId: uuid.nullable(),
    contentSha256: sha256Schema.nullable(),
    /** Cited URL when no preserved document is linked (DOCUMENT mode). */
    documentUrl: z.string().max(2_000).nullable(),
    /** Annex section: the CEST segment (two digits). */
    segment: z
        .string()
        .regex(/^\d{2}$/)
        .nullable(),
    destinationUf: ufSchema.nullable(),
    originScope: fiscalFactOriginScopeSchema.nullable(),
    originUfs: z.array(ufSchema).max(27),
    /** null = inferred (the source stated no start). */
    validFrom: z.string().date().nullable(),
    validTo: z.string().date().nullable(),
})
    .strict();
/**
 * The origin scope a group decision would cover (CONFAZ origin scope).
 * `extracted` is what the annex rows state (the group key); `default` is the
 * decision scope the desk preselects and `options` the ones the confirmation
 * accepts. A destination UF's ST annex (e.g. SP Portaria CAT 68/2019) covers
 * its internal operations; interstate retention toward it depends on the
 * CONFAZ convênio/protocolo each origin signed. While those signatories are
 * unknown (`reason: AGREEMENTS_UNKNOWN`) the default is INTERNAL_ONLY.
 */
export const fiscalFactScopeProposalSchema = z
    .object({
    extracted: z
        .object({
        originScope: fiscalFactOriginScopeSchema.nullable(),
        originUfs: z.array(ufSchema).max(27),
    })
        .strict(),
    default: fiscalFactDecisionScopeSchema,
    options: z.array(fiscalFactDecisionScopeSchema).min(1).max(3),
    /**
     * AGREEMENTS_UNKNOWN: no CONFAZ agreement in the matrix concerns the
     * segment × destination; AGREEMENT_SIGNATORIES: the matrix derives the
     * interstate origins (AGREEMENT_ORIGINS is offered and preselected).
     */
    reason: z.enum(["AGREEMENTS_UNKNOWN", "AGREEMENT_SIGNATORIES"]).nullable(),
    /** Origins the default decision covers (the destination for internal operations). */
    originUfs: z.array(ufSchema).max(27),
    /** Every origin the extraction states (ALL_ORIGINS: the 27 UFs). */
    extractedOriginUfs: z.array(ufSchema).max(27),
    /** Matrix-derived scope on `asOf` (present when AGREEMENT_ORIGINS is offered). */
    derived: z
        .object({
        asOf: z.string().date(),
        /** Destination + signatory origins bound with it on `asOf`. */
        originUfs: z.array(ufSchema).max(27),
        /** Weakest level of the agreements used (UNVERIFIED stays out of SHADOW). */
        level: z.enum(["VERIFIED", "UNVERIFIED"]),
        /** Pending facts the matrix knows / all pending facts. */
        knownFacts: z.number().int().nonnegative(),
        facts: z.number().int().nonnegative(),
        agreements: z
            .array(z
            .object({
            id: z.string().uuid(),
            key: z.string().min(1).max(40),
            title: z.string().max(1_000).nullable(),
            status: z.enum(["UNVERIFIED", "VERIFIED"]),
            validFrom: z.string().date(),
            validTo: z.string().date().nullable(),
            officialUrl: z.string().url().max(2_000),
            /** Destination bound by it on `asOf`. */
            destinationBound: z.boolean(),
        })
            .strict())
            .max(20),
        /** Membership changes in the next 365 days ("SP sai em 01/10/2026"). */
        changes: z
            .array(z
            .object({
            uf: ufSchema,
            on: z.string().date(),
            change: z.enum(["JOINS", "LEAVES"]),
            agreementKey: z.string().min(1).max(40),
        })
            .strict())
            .max(100),
    })
        .strict()
        .nullable()
        .optional(),
})
    .strict();
export const fiscalFactReviewDocumentSchema = z
    .object({
    evidenceId: uuid.nullable(),
    contentSha256: sha256Schema.nullable(),
    title: z.string().max(1_000).nullable(),
    publisher: z.string().max(500).nullable(),
    url: z.string().max(2_000).nullable(),
    host: z.string().max(253).nullable(),
    sourceKey: z.string().max(120).nullable(),
    fetchedAt: z.string().datetime().nullable(),
    /**
     * CURRENT: the preserved capture is the latest for its URL. HISTORICAL:
     * a newer capture has a different hash (the group keeps the old one).
     * UNAVAILABLE: no preserved document is linked.
     */
    state: z.enum(["CURRENT", "HISTORICAL", "UNAVAILABLE"]),
    currentSha256: sha256Schema.nullable(),
})
    .strict();
const countSchema = z.number().int().nonnegative();
export const fiscalFactReviewGroupStateSchema = z.enum([
    /** Pending facts, no open session for this reviewer. */
    "OPEN",
    /** An open session with inspections or exceptions. */
    "IN_REVIEW",
    /** A discrepancy was reported: the sample expanded, exceptions decide individually. */
    "REVIEW_REQUIRED",
    /** Nothing pending. */
    "DONE",
]);
export const fiscalFactReviewGroupSchema = z
    .object({
    id: fiscalFactReviewGroupIdSchema,
    key: fiscalFactReviewGroupKeySchema,
    /** DOCUMENT mode; SEGMENT_UF groups span documents (`documentCount`). */
    document: fiscalFactReviewDocumentSchema.nullable(),
    documentCount: countSchema,
    segmentName: z.string().max(300).nullable(),
    counts: z
        .object({
        /** Every UNVERIFIED, VERIFIED, REJECTED or CONFLICTING fact with this key. */
        facts: countSchema,
        /** UNVERIFIED: what a session can confirm. */
        pending: countSchema,
        verified: countSchema,
        rejected: countSchema,
        /** In the Em conflito queue; never confirmed by a group decision. */
        conflicting: countSchema,
        /** Pending facts with a per-fact review flag (group signals excluded). */
        flagged: countSchema,
        /** This reviewer's open session. */
        inspected: countSchema,
        exceptions: countSchema,
        discrepancies: countSchema,
    })
        .strict(),
    impact: z.object({ gapCount: countSchema, occurrenceCount: countSchema }).strict(),
    /**
     * What the group would answer once verified, independent of calculations
     * seen so far: distinct active catalog NCMs its facts cover (8-digit NCM,
     * NCM prefixes, else the CEST's prefixes) and the origin UFs they apply
     * to for the destination (ALL_ORIGINS = every other UF).
     */
    reach: z.object({ ncmCount: countSchema, routeCount: countSchema }).strict(),
    /** Properties every pending fact shares (acknowledged once at confirmation). */
    signals: z.array(fiscalFactReviewGroupSignalSchema).max(2),
    /** The origin scope a decision would cover (pending facts). */
    proposedScope: fiscalFactScopeProposalSchema,
    oldestCheckedAt: z.string().datetime().nullable(),
    state: fiscalFactReviewGroupStateSchema,
    sessionId: uuid.nullable(),
})
    .strict();
export const fiscalFactReviewGroupSortSchema = z.enum(["IMPACT", "AGE", "SIZE"]);
export const fiscalFactReviewGroupQuerySchema = z
    .object({
    mode: fiscalFactReviewGroupModeSchema.default("DOCUMENT"),
    segment: z
        .string()
        .regex(/^\d{2}$/)
        .optional(),
    destinationUf: ufSchema.optional(),
    originScope: fiscalFactOriginScopeSchema.optional(),
    source: z.enum(["IOB_SIMULADOR", "WEB_RESEARCH", "OPERATOR", "CATALOG_DERIVATION"]).optional(),
    evidenceId: uuid.optional(),
    validity: z.enum(["STATED", "INFERRED"]).optional(),
    /** WITH: groups with flagged facts or session exceptions; WITHOUT: neither. */
    exceptions: z.enum(["WITH", "WITHOUT"]).optional(),
    /** Document title, host, CEST or segment name. */
    q: z.string().trim().min(1).max(120).optional(),
    sort: fiscalFactReviewGroupSortSchema.default("IMPACT"),
})
    .strict();
export const fiscalFactReviewGroupPageSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    mode: fiscalFactReviewGroupModeSchema,
    items: z.array(fiscalFactReviewGroupSchema).max(200),
    nextCursor: z.string().min(1).nullable(),
    hasMore: z.boolean(),
    /** Groups and pending facts that match the filters (every page). */
    totals: z.object({ groups: countSchema, pending: countSchema }).strict(),
    /** Options for the filter controls, over every pending group of the mode. */
    facets: z
        .object({
        segments: z.array(z.string().regex(/^\d{2}$/)).max(100),
        destinationUfs: z.array(ufSchema).max(27),
    })
        .strict(),
})
    .strict();
/** The annex row as the worker displayed it (strings exactly as printed). */
export const fiscalFactReviewDisplayedSchema = z
    .object({
    cest: z.string().max(40).optional(),
    ncmCited: z.array(z.string().max(20)).max(50).optional(),
    originalMva: z.string().max(40).optional(),
    internalRate: z.string().max(40).optional(),
    rate: z.string().max(60).optional(),
    validFrom: z.string().max(40).optional(),
    validTo: z.string().max(40).optional(),
})
    .strict();
export const fiscalFactReviewEvidenceSchema = z
    .object({
    annexEntryId: uuid.nullable(),
    evidenceId: uuid.nullable(),
    contentSha256: sha256Schema.nullable(),
    segment: z
        .string()
        .regex(/^\d{2}$/)
        .nullable(),
    description: z.string().max(2_000).nullable(),
    /** Verbatim row (annex) or the first citation quote. */
    quote: z.string().max(4_000).nullable(),
    /** Code-point offsets of `quote` in the preserved normalized text. */
    locator: z
        .object({
        textStart: z.number().int().nonnegative(),
        textEnd: z.number().int().nonnegative(),
    })
        .strict()
        .nullable(),
    checks: z
        .object({ passed: z.boolean(), failures: z.array(z.string().max(120)).max(20) })
        .strict()
        .nullable(),
    displayed: fiscalFactReviewDisplayedSchema,
    url: z.string().max(2_000).nullable(),
    host: z.string().max(253).nullable(),
    sourceKey: z.string().max(120).nullable(),
})
    .strict();
export const fiscalFactCorroborationSchema = z
    .object({
    /** MATCH / MISMATCH against CERTIFIED IOB facts on a covered NCM × route. */
    state: z.enum(["MATCH", "MISMATCH", "NONE"]),
    iobFactIds: z.array(uuid).max(20),
    differingFields: z.array(fiscalFactFieldNameSchema).max(4),
    checkedAt: z.string().datetime().nullable(),
    /** The NCM × route the IOB answered (first certified fact). */
    ncm: ncmSchema.nullable(),
    originUf: ufSchema.nullable(),
})
    .strict();
export const fiscalFactReviewItemSchema = z
    .object({
    fact: fiscalFactSchema,
    impact: z.object({ gapCount: countSchema, occurrenceCount: countSchema }).strict(),
    flags: z.array(fiscalFactReviewFlagSchema).max(6),
    conflictingFactIds: z.array(uuid).max(50),
    evidence: fiscalFactReviewEvidenceSchema,
    corroboration: fiscalFactCorroborationSchema,
    /** An active catalog NCM the IOB can look up for this fact (corroboration). */
    iobNcm: ncmSchema.nullable(),
})
    .strict();
export const fiscalFactReviewManifestEntrySchema = z
    .object({ factId: uuid, revision: z.number().int().positive() })
    .strict();
export const fiscalFactReviewInspectionSchema = z
    .object({
    factId: uuid,
    revision: z.number().int().positive(),
    outcome: z.enum(["AGREES", "DISCREPANCY"]),
    note: z.string().max(500).nullable(),
    at: z.string().datetime(),
})
    .strict();
export const fiscalFactReviewExceptionSchema = z
    .object({ factId: uuid, reason: z.string().min(1).max(500), at: z.string().datetime() })
    .strict();
export const fiscalFactReviewSampleSchema = z
    .object({
    policyId: fiscalFactSamplePolicyIdSchema,
    seed: z.string().regex(/^[a-f0-9]{16}$/),
    expansions: z.number().int().min(0).max(FISCAL_FACT_SAMPLE_MAX_EXPANSIONS),
    /** Population after exceptions (the facts the decision would include). */
    population: countSchema,
    target: countSchema,
    /** Sorted by the population order; mandatory ∪ representatives ∪ random. */
    factIds: z.array(uuid).max(FISCAL_FACT_REVIEW_MAX_FACTS),
    mandatoryFactIds: z.array(uuid).max(FISCAL_FACT_REVIEW_MAX_FACTS),
    representativeFactIds: z.array(uuid).max(FISCAL_FACT_REVIEW_MAX_FACTS),
    randomFactIds: z.array(uuid).max(FISCAL_FACT_REVIEW_MAX_FACTS),
    whole: z.boolean(),
    /** v2: what the confirmation must acknowledge (always empty under v1). */
    groupSignals: z.array(fiscalFactReviewGroupSignalSchema).max(2),
})
    .strict();
export const fiscalFactGroupConfirmSkipReasonSchema = z.enum([
    "STALE_REVISION",
    "NOT_UNVERIFIED",
    "NOT_FOUND",
]);
export const fiscalFactGroupConfirmResultSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    sessionId: uuid,
    groupId: fiscalFactReviewGroupIdSchema,
    method: fiscalFactReviewMethodSchema,
    policyId: z.string().min(1).max(80),
    manifestDigest: sha256Schema,
    applied: z
        .array(z
        .object({
        factId: uuid,
        revision: z.number().int().positive(),
        inclusion: z.enum(["INSPECTED", "SAMPLED"]),
    })
        .strict())
        .max(FISCAL_FACT_REVIEW_MAX_FACTS),
    skipped: z
        .array(z
        .object({
        factId: uuid,
        reason: fiscalFactGroupConfirmSkipReasonSchema,
        status: z.string().max(40).nullable(),
        expectedRevision: z.number().int().positive(),
        currentRevision: z.number().int().positive().nullable(),
    })
        .strict())
        .max(FISCAL_FACT_REVIEW_MAX_FACTS),
    excluded: z
        .array(z.object({ factId: uuid, reason: z.string().max(500) }).strict())
        .max(FISCAL_FACT_REVIEW_MAX_FACTS),
    counts: z
        .object({
        manifest: countSchema,
        included: countSchema,
        applied: countSchema,
        skipped: countSchema,
        excluded: countSchema,
        inspected: countSchema,
        sampled: countSchema,
    })
        .strict(),
    /** The origin scope the decision covered (absent on results stored before it existed). */
    decisionScope: fiscalFactDecisionScopeSchema.optional(),
    /** Every origin the decision verified across its facts (absent on older results). */
    originUfs: z.array(ufSchema).max(27).optional(),
    /** AS_EXTRACTED beyond the proposal: the reviewer's acknowledgement and justification. */
    scopeAcknowledgement: fiscalFactScopeAcknowledgementSchema.nullable().optional(),
    /** True when this response replays an earlier identical confirmation. */
    replayed: z.boolean(),
    confirmedAt: z.string().datetime(),
})
    .strict();
export const fiscalFactReviewSessionSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    id: uuid,
    groupId: fiscalFactReviewGroupIdSchema,
    key: fiscalFactReviewGroupKeySchema,
    method: fiscalFactReviewMethodSchema,
    policyId: z.string().min(1).max(80),
    seed: z.string().regex(/^[a-f0-9]{16}$/),
    sampleExpansions: z.number().int().min(0).max(FISCAL_FACT_SAMPLE_MAX_EXPANSIONS),
    status: z.enum(["OPEN", "CONFIRMED", "ABANDONED"]),
    manifest: z.array(fiscalFactReviewManifestEntrySchema).max(FISCAL_FACT_REVIEW_MAX_FACTS),
    manifestDigest: sha256Schema,
    inspected: z.array(fiscalFactReviewInspectionSchema).max(FISCAL_FACT_REVIEW_MAX_FACTS),
    exceptions: z.array(fiscalFactReviewExceptionSchema).max(FISCAL_FACT_REVIEW_MAX_FACTS),
    createdBy: uuid,
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    confirmedAt: z.string().datetime().nullable(),
    result: fiscalFactGroupConfirmResultSchema.nullable(),
    revision: z.number().int().positive(),
})
    .strict();
export const fiscalFactReviewIobPlanSchema = z
    .object({
    queueable: z.boolean(),
    /** Why nothing can be queued (INTERNAL_ONLY scope, no catalog NCM…). */
    reason: z.enum(["NO_NCM", "INTERNAL_ONLY", "NOT_ST"]).nullable(),
    routes: z.array(z.object({ originUf: ufSchema, destinationUf: ufSchema }).strict()).max(5),
    /** Distinct catalog NCMs of the sample (or of the group before a session). */
    ncms: z.array(ncmSchema).max(FISCAL_FACT_REVIEW_MAX_FACTS),
})
    .strict();
export const fiscalFactReviewGroupDetailSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    group: fiscalFactReviewGroupSchema,
    /** Pending facts in manifest order (the order J / K walks). */
    items: z.array(fiscalFactReviewItemSchema).max(FISCAL_FACT_REVIEW_MAX_FACTS),
    /** The server-generated manifest a new session would fix. */
    manifest: z.array(fiscalFactReviewManifestEntrySchema).max(FISCAL_FACT_REVIEW_MAX_FACTS),
    manifestDigest: sha256Schema,
    /** More pending facts than one session carries. */
    truncated: z.boolean(),
    session: fiscalFactReviewSessionSchema.nullable(),
    /** Session manifest vs the current group (null without a session). */
    staleness: z
        .object({
        changed: z.array(uuid).max(FISCAL_FACT_REVIEW_MAX_FACTS),
        added: z.array(uuid).max(FISCAL_FACT_REVIEW_MAX_FACTS),
        removed: z.array(uuid).max(FISCAL_FACT_REVIEW_MAX_FACTS),
    })
        .strict()
        .nullable(),
    /** The sample under the session seed (or the preview seed without one). */
    sample: fiscalFactReviewSampleSchema,
    iobPlan: fiscalFactReviewIobPlanSchema,
})
    .strict();
/* -- Writes (REVIEW:TAX_RULE:ALL) ----------------------------------------------- */
export const fiscalFactReviewSessionStartSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    method: fiscalFactReviewMethodSchema,
})
    .strict();
export const fiscalFactReviewSessionActionSchema = z.discriminatedUnion("action", [
    z
        .object({
        schemaVersion: z.literal("1.0.0"),
        action: z.literal("INSPECT"),
        factId: uuid,
        revision: z.number().int().positive(),
        outcome: z.enum(["AGREES", "DISCREPANCY"]),
        note: z.string().trim().max(500).optional(),
    })
        .strict(),
    z
        .object({
        schemaVersion: z.literal("1.0.0"),
        action: z.literal("CLEAR_INSPECTION"),
        factId: uuid,
    })
        .strict(),
    z
        .object({
        schemaVersion: z.literal("1.0.0"),
        action: z.literal("MARK_EXCEPTION"),
        factId: uuid,
        reason: z.string().trim().min(FISCAL_FACT_EXCEPTION_REASON_MIN).max(500),
    })
        .strict(),
    z
        .object({
        schemaVersion: z.literal("1.0.0"),
        action: z.literal("CLEAR_EXCEPTION"),
        factId: uuid,
    })
        .strict(),
    z
        .object({
        schemaVersion: z.literal("1.0.0"),
        action: z.literal("SET_METHOD"),
        method: fiscalFactReviewMethodSchema,
    })
        .strict(),
    z.object({ schemaVersion: z.literal("1.0.0"), action: z.literal("EXPAND_SAMPLE") }).strict(),
    z.object({ schemaVersion: z.literal("1.0.0"), action: z.literal("REFRESH_MANIFEST") }).strict(),
    z.object({ schemaVersion: z.literal("1.0.0"), action: z.literal("ABANDON") }).strict(),
]);
/**
 * The exact scope the reviewer saw. The server recomputes every list from
 * the session and refuses (409) any difference, so a stale page can never
 * widen a decision.
 */
export const fiscalFactGroupConfirmSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    method: fiscalFactReviewMethodSchema,
    policyId: z.string().min(1).max(80),
    manifestDigest: sha256Schema,
    /** Facts included in this decision (manifest − exceptions) with their revisions. */
    facts: z.array(fiscalFactReviewManifestEntrySchema).min(1).max(FISCAL_FACT_REVIEW_MAX_FACTS),
    sampleFactIds: z.array(uuid).max(FISCAL_FACT_REVIEW_MAX_FACTS),
    exceptionFactIds: z.array(uuid).max(FISCAL_FACT_REVIEW_MAX_FACTS),
    /**
     * `fiscal-fact-sample-v2`: exactly the sample's `groupSignals`, which the
     * reviewer acknowledged in the dialog. Ignored (must be empty) under v1.
     */
    acknowledgedSignals: z.array(fiscalFactReviewGroupSignalSchema).max(2).optional(),
    /**
     * The origin scope the decision covers: one of the group's
     * `proposedScope.options`. Omitted = AS_EXTRACTED (clients that predate
     * decision scopes).
     */
    decisionScope: fiscalFactDecisionScopeSchema.optional(),
    /**
     * Required when the decision is AS_EXTRACTED while the proposal is
     * narrower (`requiresAllOriginsAcknowledgement`): the reviewer states the
     * document proves retention from every extracted origin, and why.
     */
    scopeAcknowledgement: fiscalFactScopeAcknowledgementSchema.optional(),
    note: z.string().trim().max(1_000).optional(),
})
    .strict();
/* -- Confirmed reviews and scope corrections ------------------------------------ */
/** What a confirmed review's facts cover today (after any correction). */
export const fiscalFactCoverageSchema = z
    .object({
    /** AS_EXTRACTED = the whole match (verifiedScope null). */
    mode: fiscalFactPreviousScopeSchema.shape.mode,
    originUfs: z.array(ufSchema).max(27),
    restricted: z.boolean(),
    facts: countSchema,
})
    .strict();
export const fiscalFactScopeCorrectionSummarySchema = z
    .object({
    id: uuid,
    restriction: fiscalFactScopeRestrictionTargetSchema,
    reason: z.string().max(1_000),
    actorId: uuid,
    correlationId: z.string().max(200),
    restricted: countSchema,
    skipped: countSchema,
    at: z.string().datetime(),
})
    .strict();
/** One confirmed group review (history, "Corrigir escopo"). */
export const fiscalFactConfirmedReviewSchema = z
    .object({
    sessionId: uuid,
    groupId: fiscalFactReviewGroupIdSchema,
    key: fiscalFactReviewGroupKeySchema,
    documentTitle: z.string().max(1_000).nullable(),
    segmentName: z.string().max(300).nullable(),
    method: fiscalFactReviewMethodSchema,
    confirmedBy: uuid,
    confirmedAt: z.string().datetime(),
    /** null: confirmed before decision scopes were stored (AS_EXTRACTED). */
    decisionScope: fiscalFactDecisionScopeSchema.nullable(),
    scopeAcknowledgement: fiscalFactScopeAcknowledgementSchema.nullable(),
    applied: countSchema,
    /** Applied facts that are still VERIFIED. */
    verified: countSchema,
    coverage: z.array(fiscalFactCoverageSchema).max(50),
    /**
     * The decision covers interstate origins while the proposal today is
     * narrower (all origins without verified CONFAZ signatories).
     */
    wideScope: z.boolean(),
    /** Origins every still-verified fact states (the correction's choices). */
    extractedOriginUfs: z.array(ufSchema).max(27),
    corrections: z.array(fiscalFactScopeCorrectionSummarySchema).max(50),
})
    .strict();
export const fiscalFactConfirmedReviewPageSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    items: z.array(fiscalFactConfirmedReviewSchema).max(200),
    nextCursor: z.string().min(1).nullable(),
    hasMore: z.boolean(),
})
    .strict();
/* -- Pure logic --------------------------------------------------------------------- */
/** Deterministic 64-bit FNV-1a (two lanes) as 16 hex. A locator, not a security digest. */
export function fnv1a64(value) {
    let a = 0x811c9dc5;
    let b = 0x01000193 ^ 0x9e3779b9;
    for (let index = 0; index < value.length; index += 1) {
        const code = value.charCodeAt(index);
        a ^= code;
        a = Math.imul(a, 0x01000193) >>> 0;
        b ^= code;
        b = Math.imul(b, 0x01000193 ^ 0x5bd1e995) >>> 0;
    }
    return a.toString(16).padStart(8, "0") + b.toString(16).padStart(8, "0");
}
function sortedUfs(values) {
    return [...new Set(values ?? [])].sort();
}
export function reviewGroupKey(input, mode) {
    const { fact, annex } = input;
    const match = fact.match;
    const citation = fact.citations[0];
    const segment = annex?.segment ?? (match.cest ? match.cest.slice(0, 2) : null);
    const destinationUf = match.destinationUf ?? null;
    if (mode === "SEGMENT_UF") {
        return {
            mode,
            kind: fact.kind,
            evidenceId: null,
            contentSha256: null,
            documentUrl: null,
            segment,
            destinationUf,
            originScope: null,
            originUfs: [],
            validFrom: null,
            validTo: null,
        };
    }
    const evidenceId = annex?.evidenceId ?? citation?.evidenceId ?? null;
    return {
        mode,
        kind: fact.kind,
        evidenceId,
        contentSha256: annex?.contentSha256 ?? null,
        documentUrl: evidenceId ? null : (citation?.url ?? null),
        segment,
        destinationUf,
        originScope: match.originScope ?? (match.originUf ? "ORIGINS" : null),
        originUfs: sortedUfs(match.originUfs ?? (match.originUf ? [match.originUf] : [])),
        validFrom: fact.validFromStated ? fact.validFrom : null,
        validTo: fact.validTo,
    };
}
export function reviewGroupId(key) {
    const canonical = JSON.stringify([
        key.mode,
        key.kind,
        key.evidenceId,
        key.contentSha256,
        key.documentUrl,
        key.segment,
        key.destinationUf,
        key.originScope,
        key.originUfs,
        key.validFrom,
        key.validTo,
    ]);
    return `${key.mode === "DOCUMENT" ? "d" : "s"}-${fnv1a64(canonical)}`;
}
export function reviewGroupModeOf(groupId) {
    return groupId.startsWith("s-") ? "SEGMENT_UF" : "DOCUMENT";
}
/** Seed of the reproducible sample: fixed by the policy, the group and its manifest. */
export function reviewSampleSeed(groupId, manifestDigest, policyId = FISCAL_FACT_SAMPLE_POLICY_ID) {
    return fnv1a64(`${policyId}:${groupId}:${manifestDigest}`);
}
/** mulberry32 over the 16-hex seed. */
function prng(seed) {
    let state = (Number.parseInt(seed.slice(0, 8), 16) ^ Number.parseInt(seed.slice(8), 16)) >>> 0;
    return () => {
        state = (state + 0x6d2b79f5) >>> 0;
        let t = state;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4_294_967_296;
    };
}
function fieldShape(fields) {
    return ["stApplicable", "originalMva", "destinationInternalRate", "interstateRate"]
        .filter((field) => fields[field] !== undefined)
        .join("+");
}
/** Target size: whole group up to 10; else 15 % (10–60), +50 % per expansion. */
export function sampleTarget(population, expansions) {
    if (population <= FISCAL_FACT_SAMPLE_WHOLE_GROUP_MAX)
        return population;
    const base = Math.min(FISCAL_FACT_SAMPLE_MAX, Math.max(FISCAL_FACT_SAMPLE_MIN, Math.ceil(population * FISCAL_FACT_SAMPLE_RATE)));
    return Math.min(population, base + expansions * Math.ceil(base / 2));
}
/**
 * Group signals of a population (`fiscal-fact-sample-v2`): a group-level flag
 * every candidate carries, and one origin scope shared by all of them.
 * Empty for an empty population.
 */
export function reviewGroupSignals(population) {
    const first = population[0];
    if (!first)
        return [];
    const signals = [];
    if (population.every((candidate) => candidate.flags.includes("INFERRED_VALIDITY"))) {
        signals.push("INFERRED_VALIDITY");
    }
    const scopeOf = (candidate) => `${candidate.originScope ?? ""}:${[...candidate.originUfs].sort().join(",")}`;
    const scope = scopeOf(first);
    if (first.originScope && population.every((candidate) => scopeOf(candidate) === scope)) {
        signals.push("ORIGIN_SCOPE");
    }
    return signals;
}
/** The flags that make a fact mandatory under a policy, given the group signals. */
export function mandatoryFlags(flags, policyId, signals) {
    if (policyId === FISCAL_FACT_SAMPLE_POLICY_V1)
        return [...flags];
    return flags.filter((flag) => !(FISCAL_FACT_GROUP_LEVEL_FLAGS.includes(flag) &&
        signals.includes(flag)));
}
/**
 * Sampling policy, reproducible from (population, seed, expansions, policy):
 * 1. Mandatory: every fact with a per-fact review flag. Under v1 every flag
 *    counts; under v2 a group-level flag shared by the whole population
 *    (inferred validity) is a group signal instead (`groupSignals`).
 * 2. Representatives, deterministic: the first fact (by CEST, then id) of
 *    each distinct field shape × origin scope and of each NCM chapter, so
 *    CEST/NCM variants, extracted fields and scope are all covered.
 * 3. Random: the rest of the population in a seeded shuffle, until the
 *    target is reached. Expanding only appends to that order.
 * Exceptions are not in the population: they are decided individually.
 */
export function computeReviewSample(population, seed, expansions, policyId = FISCAL_FACT_SAMPLE_POLICY_ID) {
    const groupSignals = policyId === FISCAL_FACT_SAMPLE_POLICY_V1 ? [] : reviewGroupSignals(population);
    const ordered = [...population].sort((left, right) => (left.cest ?? "").localeCompare(right.cest ?? "") || left.factId.localeCompare(right.factId));
    const target = sampleTarget(ordered.length, expansions);
    const chosen = new Set();
    const mandatory = [];
    const representatives = [];
    const random = [];
    const whole = ordered.length <= FISCAL_FACT_SAMPLE_WHOLE_GROUP_MAX;
    for (const candidate of ordered) {
        if (whole || mandatoryFlags(candidate.flags, policyId, groupSignals).length > 0) {
            mandatory.push(candidate.factId);
            chosen.add(candidate.factId);
        }
    }
    if (!whole) {
        const strata = new Set();
        for (const candidate of ordered) {
            const scope = `${candidate.originScope ?? ""}:${[...candidate.originUfs].sort().join(",")}`;
            const chapter = (candidate.ncmPrefixes[0] ?? candidate.cest ?? "")
                .replace(/\D/g, "")
                .slice(0, 2);
            const keys = [`shape:${fieldShape(candidate.fields)}|${scope}`, `chapter:${chapter}`];
            const fresh = keys.filter((key) => !strata.has(key));
            if (fresh.length === 0)
                continue;
            for (const key of fresh)
                strata.add(key);
            if (!chosen.has(candidate.factId)) {
                representatives.push(candidate.factId);
                chosen.add(candidate.factId);
            }
        }
        const next = prng(seed);
        const shuffled = [...ordered];
        for (let index = shuffled.length - 1; index > 0; index -= 1) {
            const swap = Math.floor(next() * (index + 1));
            [shuffled[index], shuffled[swap]] = [
                shuffled[swap],
                shuffled[index],
            ];
        }
        for (const candidate of shuffled) {
            if (chosen.size >= target)
                break;
            if (chosen.has(candidate.factId))
                continue;
            random.push(candidate.factId);
            chosen.add(candidate.factId);
        }
    }
    const factIds = ordered.filter((c) => chosen.has(c.factId)).map((c) => c.factId);
    return {
        policyId,
        seed,
        expansions,
        population: ordered.length,
        target: Math.max(target, factIds.length),
        factIds,
        mandatoryFactIds: mandatory,
        representativeFactIds: representatives,
        randomFactIds: random,
        whole,
        groupSignals,
    };
}
/**
 * What a confirmation would do now, and what stops it. Shared by the API
 * (it refuses when `ready` is false) and the review desk (it explains why).
 * Facts that changed after the session started do not block: the
 * confirmation skips them and reports each one (partial result).
 */
export function reviewDecision(input) {
    const { session, sample } = input;
    const exceptionIds = session.exceptions.map((entry) => entry.factId);
    const exceptionSet = new Set(exceptionIds);
    const inspection = new Map(session.inspected.map((entry) => [entry.factId, entry]));
    const included = session.manifest.filter((entry) => !exceptionSet.has(entry.factId));
    const agrees = (entry) => {
        const found = inspection.get(entry.factId);
        return found?.outcome === "AGREES" && found.revision === entry.revision;
    };
    const inspectedIds = included.filter(agrees).map((entry) => entry.factId);
    const inspectedSet = new Set(inspectedIds);
    const sampleSet = new Set(sample.factIds);
    const required = session.method === "FULL" ? included : included.filter((entry) => sampleSet.has(entry.factId));
    const pendingIds = required.filter((entry) => !agrees(entry)).map((entry) => entry.factId);
    const discrepancies = session.inspected.filter((entry) => entry.outcome === "DISCREPANCY").length;
    const blockers = [];
    if (session.status !== "OPEN")
        blockers.push("SESSION_NOT_OPEN");
    if (included.length === 0)
        blockers.push("NOTHING_TO_CONFIRM");
    if (session.method === "SAMPLE") {
        if (session.key.mode !== "DOCUMENT")
            blockers.push("SAMPLE_NOT_ALLOWED");
        if (discrepancies >= FISCAL_FACT_SAMPLE_DISCREPANCY_LIMIT)
            blockers.push("TOO_MANY_DISCREPANCIES");
    }
    if (pendingIds.length > 0)
        blockers.push("UNINSPECTED");
    return {
        method: session.method,
        included,
        inspectedIds,
        sampledIds: session.method === "SAMPLE"
            ? included.filter((entry) => !inspectedSet.has(entry.factId)).map((entry) => entry.factId)
            : [],
        exceptionIds,
        pendingIds,
        discrepancies,
        blockers,
        ready: blockers.length === 0,
    };
}
/** The confirm body that matches a session exactly (what the desk sends). */
export function groupConfirmBody(input) {
    const note = input.note?.trim();
    const acknowledged = input.session.policyId === FISCAL_FACT_SAMPLE_POLICY_V1
        ? []
        : input.sample.groupSignals.filter((signal) => (input.acknowledgedSignals ?? []).includes(signal));
    return {
        schemaVersion: "1.0.0",
        method: input.session.method,
        policyId: input.session.policyId,
        manifestDigest: input.session.manifestDigest,
        facts: input.decision.included,
        sampleFactIds: input.session.method === "SAMPLE" ? [...input.sample.factIds] : [],
        exceptionFactIds: [...input.decision.exceptionIds],
        ...(acknowledged.length ? { acknowledgedSignals: acknowledged } : {}),
        ...(input.decisionScope ? { decisionScope: input.decisionScope } : {}),
        ...(input.scopeAcknowledgement
            ? {
                scopeAcknowledgement: {
                    allOriginsProven: true,
                    justification: input.scopeAcknowledgement.justification.trim(),
                },
            }
            : {}),
        ...(note ? { note } : {}),
    };
}
/** v2: every group signal of the sample must be acknowledged before confirming. */
export function unacknowledgedSignals(policyId, sample, acknowledged) {
    if (policyId === FISCAL_FACT_SAMPLE_POLICY_V1)
        return [];
    return sample.groupSignals.filter((signal) => !(acknowledged ?? []).includes(signal));
}
/* -- Origin scope of a decision (CONFAZ) -------------------------------------------- */
const ALL_UF_CODES = ufSchema.options;
/** Origin UFs a fact's own match states for its destination (ALL_ORIGINS: all 27). */
export function extractedOriginUfs(fact) {
    const { match } = fact;
    if (match.originUf)
        return [match.originUf];
    switch (match.originScope) {
        case "ORIGINS":
            return [...new Set(match.originUfs ?? [])].sort();
        case "INTERNAL_ONLY":
            return match.destinationUf ? [match.destinationUf] : [];
        default:
            return [...ALL_UF_CODES];
    }
}
/** A ST_SEGMENT fact that states internal operations of its destination and some interstate route. */
function narrowable(fact) {
    const destination = fact.match.destinationUf;
    if (fact.kind !== "ST_SEGMENT" || !destination || fact.match.originUf)
        return false;
    const origins = extractedOriginUfs(fact);
    return origins.includes(destination) && origins.some((uf) => uf !== destination);
}
const statesAllOrigins = (fact) => !fact.match.originScope || fact.match.originScope === "ALL_ORIGINS";
/** The segment × CEST a fact is governed by (annex section, else the CEST's first digits). */
export function factAgreementTarget(fact) {
    const cest = fact.match.cest ?? null;
    return { segment: fact.segment ?? cest?.slice(0, 2) ?? null, cest };
}
/** Matrix-derived origins of one fact on `asOf` (null when the matrix does not know it). */
export function factDerivedOrigins(fact, matrix) {
    const destinationUf = fact.match.destinationUf;
    if (!destinationUf || fact.kind !== "ST_SEGMENT" || !statesAllOrigins(fact))
        return null;
    const derived = derivedOriginScope(matrix.agreements, {
        ...factAgreementTarget(fact),
        destinationUf,
        date: matrix.asOf,
    });
    return derived.known ? derived : null;
}
const addYear = (date) => {
    const value = new Date(`${date}T00:00:00.000Z`);
    value.setUTCFullYear(value.getUTCFullYear() + 1);
    return value.toISOString().slice(0, 10);
};
/**
 * Decision scope proposal for a group's pending facts:
 * - every pending fact is a ST_SEGMENT fact covering its destination's
 *   internal operations and some interstate route → INTERNAL_ONLY is offered;
 * - facts stating all origins (or no scope): with a matrix that knows the
 *   segment × destination, AGREEMENT_ORIGINS is offered and preselected
 *   (internal + signatory origins on each date); otherwise INTERNAL_ONLY is
 *   the default (`AGREEMENTS_UNKNOWN`);
 * - otherwise only AS_EXTRACTED.
 */
export function reviewScopeProposal(facts, extracted, matrix) {
    const extractedUfs = [...new Set(facts.flatMap((fact) => extractedOriginUfs(fact)))].sort();
    const destinations = [
        ...new Set(facts.flatMap((fact) => (fact.match.destinationUf ? [fact.match.destinationUf] : []))),
    ].sort();
    const base = {
        extracted: {
            originScope: extracted.originScope ?? null,
            originUfs: [...extracted.originUfs].sort(),
        },
        extractedOriginUfs: extractedUfs,
    };
    const canNarrow = facts.length > 0 && facts.every(narrowable);
    if (!canNarrow) {
        return fiscalFactScopeProposalSchema.parse({
            ...base,
            default: "AS_EXTRACTED",
            options: ["AS_EXTRACTED"],
            reason: null,
            originUfs: extractedUfs,
            derived: null,
        });
    }
    const unknown = facts.some(statesAllOrigins);
    if (unknown && matrix) {
        const derivations = facts.map((fact) => factDerivedOrigins(fact, matrix));
        const known = derivations.filter((entry) => entry !== null);
        if (known.length > 0) {
            const first = known[0];
            const common = first.originUfs.filter((uf) => known.every((entry) => entry.originUfs.includes(uf)));
            const keys = [...new Set(known.flatMap((entry) => entry.agreementKeys))].sort();
            const used = matrix.agreements.filter((agreement) => keys.includes(agreement.key) &&
                (agreement.status === "VERIFIED" || agreement.status === "UNVERIFIED") &&
                effectiveAgreements(matrix.agreements).some((entry) => entry.id === agreement.id));
            const targets = [
                ...new Map(facts.map((fact) => {
                    const target = factAgreementTarget(fact);
                    return [`${target.segment}:${target.cest}`, target];
                })).values(),
            ];
            const horizon = addYear(matrix.asOf);
            const changes = [
                ...new Map(targets
                    .flatMap((target) => agreementChangesBetween(used, target, matrix.asOf, horizon))
                    .map((change) => [
                    `${change.uf}:${change.on}:${change.change}:${change.agreementKey}`,
                    change,
                ])).values(),
            ].slice(0, 100);
            return fiscalFactScopeProposalSchema.parse({
                ...base,
                default: "AGREEMENT_ORIGINS",
                options: ["AGREEMENT_ORIGINS", "INTERNAL_ONLY", "AS_EXTRACTED"],
                reason: "AGREEMENT_SIGNATORIES",
                originUfs: common,
                derived: {
                    asOf: matrix.asOf,
                    originUfs: common,
                    level: known.some((entry) => entry.level === "UNVERIFIED") ? "UNVERIFIED" : "VERIFIED",
                    knownFacts: known.length,
                    facts: facts.length,
                    agreements: used.slice(0, 20).map((agreement) => ({
                        id: agreement.id,
                        key: agreement.key,
                        title: agreement.title,
                        status: agreement.status,
                        validFrom: agreement.validFrom,
                        validTo: agreement.validTo,
                        officialUrl: agreement.officialUrl,
                        destinationBound: destinations.some((uf) => agreementMembersOn(agreement, matrix.asOf).includes(uf)),
                    })),
                    changes,
                },
            });
        }
    }
    return fiscalFactScopeProposalSchema.parse({
        ...base,
        default: unknown ? "INTERNAL_ONLY" : "AS_EXTRACTED",
        options: unknown ? ["INTERNAL_ONLY", "AS_EXTRACTED"] : ["AS_EXTRACTED", "INTERNAL_ONLY"],
        reason: unknown ? "AGREEMENTS_UNKNOWN" : null,
        originUfs: unknown ? destinations : extractedUfs,
        derived: null,
    });
}
/** Origins a decision scope covers for one fact (AGREEMENT_ORIGINS: on the matrix date). */
export function decisionOriginUfs(fact, scope, matrix) {
    const destinationUf = fact.match.destinationUf;
    if (scope === "INTERNAL_ONLY" && destinationUf)
        return [destinationUf];
    if (scope === "AGREEMENT_ORIGINS" && destinationUf) {
        const derived = matrix ? factDerivedOrigins(fact, matrix) : null;
        return derived ? derived.originUfs : [destinationUf];
    }
    return extractedOriginUfs(fact);
}
/* -- Hardening: all origins beyond the proposal ------------------------------------ */
/**
 * AS_EXTRACTED (explicit or omitted) covers more origins than the proposal:
 * every extracted origin while the proposal is internal-only (CONFAZ
 * signatories not verified) or the agreement signatories. The confirmation
 * then needs `scopeAcknowledgement` ("O documento prova retenção de todas as
 * UFs" plus a justification).
 */
export function requiresAllOriginsAcknowledgement(proposal, decisionScope) {
    return ((decisionScope ?? "AS_EXTRACTED") === "AS_EXTRACTED" && proposal.default !== "AS_EXTRACTED");
}
/** Why a scope acknowledgement is refused (null = fine or not needed). */
export function scopeAcknowledgementProblem(proposal, decisionScope, acknowledgement) {
    const required = requiresAllOriginsAcknowledgement(proposal, decisionScope);
    if (required && !acknowledgement)
        return "REQUIRED";
    if (!required && acknowledgement)
        return "UNEXPECTED";
    return null;
}
/* -- Scope correction ("Restringir escopo de origem") ------------------------------ */
/** The origins a verification covers today (AGREEMENT_ORIGINS: its snapshot). */
export function coveredScope(fact) {
    const scope = fact.verifiedScope ?? null;
    if (!scope)
        return { mode: "AS_EXTRACTED", originUfs: [...extractedOriginUfs(fact)].sort() };
    return { mode: scope.mode, originUfs: [...scope.originUfs].sort() };
}
/**
 * The narrowed verified scope a correction stores on one VERIFIED fact, or
 * why it does not apply. A correction only removes origins: the target must
 * be inside what the fact states and what its verification covers today, and
 * differ from it. The result is a hard cap (the matrix never widens it).
 */
export function restrictedScope(fact, target, mark) {
    if (fact.status !== "VERIFIED")
        return { ok: false, reason: "NOT_VERIFIED" };
    const destinationUf = fact.match.destinationUf;
    if (fact.kind !== "ST_SEGMENT" || !destinationUf || fact.match.originUf) {
        return { ok: false, reason: "NOT_ST_SEGMENT" };
    }
    const wanted = (target.mode === "INTERNAL_ONLY" ? [destinationUf] : [...new Set(target.originUfs)]).sort();
    const extracted = extractedOriginUfs(fact);
    if (wanted.some((uf) => !extracted.includes(uf))) {
        return { ok: false, reason: "OUTSIDE_EXTRACTED" };
    }
    const previous = coveredScope(fact);
    const within = wanted.every((uf) => previous.originUfs.includes(uf)) &&
        wanted.length < previous.originUfs.length;
    if (!within) {
        // Same origins (or a wider list) than the verification covers today.
        return {
            ok: false,
            reason: wanted.every((uf) => previous.originUfs.includes(uf))
                ? "ALREADY_WITHIN"
                : "WIDENS",
        };
    }
    const restriction = {
        correctionId: mark.correctionId,
        reason: mark.reason,
        restrictedAt: mark.restrictedAt,
        restrictedBy: mark.restrictedBy,
        previous,
    };
    const internal = wanted.length === 1 && wanted[0] === destinationUf;
    const originUfs = wanted;
    return {
        ok: true,
        previous,
        scope: internal
            ? {
                mode: "INTERNAL_ONLY",
                originUfs,
                decidedAt: mark.restrictedAt,
                reviewSessionId: mark.reviewSessionId,
                restriction,
            }
            : {
                mode: "ORIGINS",
                originUfs,
                decidedAt: mark.restrictedAt,
                reviewSessionId: mark.reviewSessionId,
                restriction,
            },
    };
}
//# sourceMappingURL=fiscal-fact-review.js.map